import { EDatasourceType, IActionDefinitionDto, IActionFolder, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { FeatureGates } from "../../../../shared/utility/feature-gates";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherFolder,
  KuikaEntitySwitcherGroup,
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { canManageActions, deleteActionFromRow, removeActionFromItsFolder } from "../datasource-row-actions";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on an AI editor's bar: which function is open, and the way to every other
 * AI function in the project — actions, agents and vision actions together, in the AI Hub's own
 * folders. It is one hub, and the three editors are three ways into it; opening an agent from
 * an action's bar opens the agent editor, because the record carries its own datasource.
 *
 * The kinds are the chips; the "…" offers what the hub offers a function row — Move out of
 * folder, and Delete behind the same confirmation. A pick is PARKED as the editor's pending
 * close, so its guard offers to save first.
 */

const AI_TYPES: EDatasourceType[] = [EDatasourceType.AIActions, EDatasourceType.AIAgent, EDatasourceType.VisionAction];

interface IAiKind {
  type: EDatasourceType;
  label: string;
}

/** The hub's own tabs, in its order. Their glyphs are `getEditorGlyph`'s, all in the AI family's purple. */
const AI_KINDS: IAiKind[] = [
  { type: EDatasourceType.AIActions, label: "AI Actions" },
  { type: EDatasourceType.AIAgent, label: "AI Agents" },
  { type: EDatasourceType.VisionAction, label: "Vision Actions" }
];

const kindIcon = (actionType: EDatasourceType): React.ReactNode => (
  <EditorGlyphIcon glyph={getEditorGlyph({ kind: "ai", actionType })} />
);

const toFolder = (folder: IActionFolder): KuikaEntitySwitcherFolder => ({
  id: folder.id,
  name: folder.name,
  itemIds: folder.actionIds ?? [],
  children: (folder.children ?? []).map(toFolder)
});

export interface IAiActionSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  /** The kind of the editor this chip sits in — it draws the trigger's glyph. */
  actionType: EDatasourceType;
  currentActionId: string | undefined;
  currentName: string;
  /**
   * What sets the open record apart within its kind, said in the glyph's tooltip after the kind:
   * an agent's process type ("AI Agent · Hierarchical"). It is a fact, not a control, so it has no
   * badge of its own on the bar.
   */
  glyphDetail?: string;
  /** The editor's guarded close — it leads to the AI hub. */
  onManageAll: () => void;
  triggerTestId?: string;
}

export const AiActionSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  actionType,
  currentActionId,
  currentName,
  glyphDetail,
  onManageAll,
  triggerTestId
}: IAiActionSwitcherChipProps): React.ReactElement => {
  const { deleteOption, deleteModal }: ISwitcherDelete<IActionDefinitionDto> = useSwitcherDelete(
    "AI function",
    (action: IActionDefinitionDto): void => void deleteActionFromRow(sd, action)
  );

  // The kinds this workspace can reach — agents and vision sit behind an allowlist.
  const kinds: IAiKind[] = AI_KINDS.filter((kind: IAiKind): boolean =>
    FeatureGates.canAccessAiDatasourceType(kind.type)
  );
  const glyph: IEditorGlyph = getEditorGlyph({ kind: "ai", actionType, detail: glyphDetail });

  const records: IActionDefinitionDto[] = (sd.actionDefinitions ?? []).filter(
    (action: IActionDefinitionDto): boolean =>
      !!action.id && kinds.some((kind: IAiKind): boolean => kind.type === action.actionType)
  );
  const items: KuikaEntitySwitcherItem[] = records
    .map((action: IActionDefinitionDto): KuikaEntitySwitcherItem => ({ id: action.id, name: action.name }))
    .sort((a: KuikaEntitySwitcherItem, b: KuikaEntitySwitcherItem): number => a.name.localeCompare(b.name));
  const find = (id: string): IActionDefinitionDto | undefined =>
    records.find((candidate: IActionDefinitionDto): boolean => candidate.id === id);

  // The hub's folders: top-level folders that belong to an AI datasource, sub-folders under them.
  const aiDatasourceIds: string[] = (sd.datasources ?? [])
    .filter((datasource: IDatasourceDto): boolean => AI_TYPES.includes(datasource.datasourceType))
    .map((datasource: IDatasourceDto): string => datasource.id);
  const folders: KuikaEntitySwitcherFolder[] = (sd.project?.actionFolders ?? [])
    .filter(
      (folder: IActionFolder): boolean => !folder.parentFolderId && aiDatasourceIds.includes(folder.dataSourceType)
    )
    .map(toFolder);
  const filedIds: Set<string> = new Set<string>();
  const collect = (list: KuikaEntitySwitcherFolder[]): void =>
    list.forEach((folder: KuikaEntitySwitcherFolder): void => {
      folder.itemIds.forEach((id: string): void => void filedIds.add(id));
      collect(folder.children ?? []);
    });
  collect(folders);

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={currentName}
        triggerTestId={triggerTestId}
        items={items}
        current={currentActionId ? { id: currentActionId, name: currentName } : undefined}
        entityLabel="AI Function"
        iconName={glyph.iconName}
        iconType={glyph.iconType}
        getItemIcon={(item: KuikaEntitySwitcherItem): React.ReactNode =>
          kindIcon(find(item.id)?.actionType ?? actionType)
        }
        folders={folders}
        // One chip per kind only when there is more than one to choose between.
        groups={
          kinds.length > 1
            ? kinds.map((kind: IAiKind): KuikaEntitySwitcherGroup => ({ key: String(kind.type), label: kind.label }))
            : undefined
        }
        getItemGroup={(item: KuikaEntitySwitcherItem): string => String(find(item.id)?.actionType ?? "")}
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem) => {
          const action: IActionDefinitionDto | undefined = find(item.id);
          // The hub offers nothing on a function row to a role that cannot manage them.
          if (!action || !canManageActions(sd)) return [];
          return [
            {
              key: "moveOut",
              label: "Move out of folder",
              icon: <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />,
              hidden: !filedIds.has(action.id),
              onClick: (): void => removeActionFromItsFolder(sd, action.id)
            },
            deleteOption(action)
          ];
        }}
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const action: IActionDefinitionDto | undefined = find(item.id);
          const datasource: IDatasourceDto | undefined = (sd.datasources ?? []).find(
            (candidate: IDatasourceDto): boolean => candidate.id === action?.kDatasource_Id
          );
          if (!action || !datasource) return;
          setNextCallbackFunction((): void => sd.setEditingActionsDatasource(datasource, action, false, false));
        }}
      />
      {deleteModal}
    </>
  );
};
